'use client';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormSelect } from '@/components/forms/form-select';
import { createAssignRoleSchema, type AssignRoleFormValues } from '../schemas';
import { getRoleDisplayName } from '../mappers/role.mapper';
import { useAssignRoleMutation } from '../services/roles.mutations';
import { userQueries } from '../services/roles.queries';
import type { Role } from '../types';

interface AssignRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role | null;
}

export function AssignRoleDialog({
  open,
  onOpenChange,
  role
}: AssignRoleDialogProps) {
  const t = useTranslations('roles');
  const tv = useTranslations('validation');
  const locale = useLocale();

  const schema = useMemo(() => createAssignRoleSchema(tv), [tv]);

  const form = useForm<AssignRoleFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      user_id: ''
    }
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    ...userQueries.list(),
    enabled: open
  });

  const userOptions = useMemo(() => {
    const users = usersData?.data ?? [];
    return users.map((user) => ({
      value: String(user.id),
      label: `${user.name} (${user.email})`
    }));
  }, [usersData]);

  const assignMutation = useAssignRoleMutation();

  async function onSubmit(values: AssignRoleFormValues) {
    if (!role) return;
    try {
      await assignMutation.mutateAsync({
        userId: Number(values.user_id),
        data: { role: role.name }
      });
      onOpenChange(false);
      form.reset();
    } catch {
      // handled by global MutationCache
    }
  }

  function handleOpenChange(value: boolean) {
    if (!value) {
      form.reset();
    }
    onOpenChange(value);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>{t('assignRole')}</DialogTitle>
          <DialogDescription>
            {t('assignRoleDescription', {
              name: role ? getRoleDisplayName(role, locale) : ''
            })}
          </DialogDescription>
        </DialogHeader>

        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className='space-y-6'>
            {usersLoading ? (
              <div className='flex items-center justify-center py-8'>
                <Loader2 className='h-6 w-6 animate-spin' />
              </div>
            ) : (
              <FormSelect
                control={form.control}
                name='user_id'
                label={t('selectUser')}
                placeholder={t('selectUserPlaceholder')}
                options={userOptions}
                required
              />
            )}

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => handleOpenChange(false)}
              >
                {t('cancel')}
              </Button>
              <Button type='submit' disabled={assignMutation.isPending}>
                {assignMutation.isPending && (
                  <Loader2 className='me-2 h-4 w-4 animate-spin' />
                )}
                {t('assign')}
              </Button>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
